/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Button, Form, Icon, Input } from '@mairistem/composants';

import { Qualification } from '@/enums';
import type {
  Diplome,
  Individu,
  Profession,
  SituationScolaire,
  Specialite,
} from '@/types';
import type {
  BaseQualification,
  QualificationUnion,
} from '@/types/QualificationsType';

import {
  DiplomeListeContainer,
  ProfessionListeContainer,
  SituationScolaireListeContainer,
  SpecialiteListeContainer,
} from '@/components/Listes';

import { QUALIFICATION_ATTRIBUTES } from '@/constants';

import { ListeParametrableModal } from '@mairistem/liste';

interface QualificationInputProps {
  onChange: (event: React.SyntheticEvent, individu: Partial<Individu>) => void;
  qualification: Qualification;
  value: Diplome | SituationScolaire | Specialite | Profession;
  disabled?: boolean;
}

const QualificationInput = (props: QualificationInputProps) => {
  const { onChange, qualification, value, disabled = false } = props;

  const [modalOpen, setModalOpen] = React.useState<Qualification | null>();
  const [selectedItem, setSelectedItem] =
    React.useState<QualificationUnion>(null);

  const [inputValue, setInputValue] = React.useState<string>('');

  React.useEffect(() => {
    setSelectedItem(value);
    setInputValue(value?.libelle || '');
  }, [value]);

  const handleSelect = React.useCallback((item: BaseQualification) => {
    setSelectedItem(item);
  }, []);

  const selectedItemRef = React.useRef<QualificationUnion>();

  // Keep the ref updated with the latest selectedItem value
  React.useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  const handleSave = React.useCallback(() => {
    const currentSelectedItem = selectedItemRef.current;

    if (currentSelectedItem) {
      onChange(null, {
        // @ts-ignore
        name: QUALIFICATION_ATTRIBUTES[qualification]?.name,
        value: currentSelectedItem,
      });
    }

    setModalOpen(null);
  }, [onChange, qualification]);

  const handleChange = React.useCallback(
    (item: QualificationUnion) => {
      if (onChange) {
        onChange(null, {
          // @ts-ignore
          name: QUALIFICATION_ATTRIBUTES[qualification]?.name,
          value: item,
        });
      }

      setSelectedItem(item);

      setModalOpen(null);
    },
    [onChange, qualification],
  );

  const handleReset = React.useCallback(() => {
    setInputValue('');
    setSelectedItem(null);
    onChange(null, {
      // @ts-ignore
      name: QUALIFICATION_ATTRIBUTES[qualification]?.name,
      value: null,
    });
  }, [qualification, onChange]);

  const handleModalClose = React.useCallback(() => {
    setModalOpen(null);
    // Allows the input to keep the right value if you select another one but don't save it
    if (value) {
      setSelectedItem(value);
      setInputValue(value.libelle || '');
    }
  }, [value]);

  const listToRender = React.useMemo(() => {
    switch (qualification) {
      case Qualification.Diplome:
        return (
          <DiplomeListeContainer
            onSave={handleChange}
            onSelect={handleSelect}
            resetSelectedItem={() => setSelectedItem(null)}
            selectedItem={selectedItem}
          />
        );

      case Qualification.Specialite:
        return (
          <SpecialiteListeContainer
            onSave={handleChange}
            onSelect={handleSelect}
            resetSelectedItem={() => setSelectedItem(null)}
            selectedItem={selectedItem}
          />
        );

      case Qualification.SituationScolaire:
        return (
          <SituationScolaireListeContainer
            onSave={handleChange}
            onSelect={handleSelect}
            resetSelectedItem={() => setSelectedItem(null)}
            selectedItem={selectedItem}
          />
        );

      case Qualification.Profession:
        return (
          <ProfessionListeContainer
            onSave={handleChange}
            onSelect={handleSelect}
            resetSelectedItem={() => setSelectedItem(null)}
            selectedItem={selectedItem}
          />
        );

      default:
        return <h1>Une erreur est survenue...</h1>;
    }
  }, [qualification, handleChange, handleSelect, selectedItem]);

  return (
    <Form>
      <Input
        readOnly
        label={QUALIFICATION_ATTRIBUTES[qualification]?.title}
        value={inputValue}
        action
        disabled={disabled}
      >
        <input />
        {!_.isEmpty(inputValue) && !disabled && (
          <Button
            basic
            data-testid="qualification-input-reset-button"
            onClick={handleReset}
            type="button"
            style={{
              padding: '1em',
              position: 'absolute',
              right: 40,
              boxShadow: 'none',
            }}
          >
            <Icon name="close" style={{ marginRight: -1 }} />
          </Button>
        )}
        <Button
          basic
          data-testid="qualification-input-search-button"
          onClick={() => setModalOpen(qualification)}
          type="button"
          style={{ padding: '1em' }}
          disabled={disabled}
        >
          <Icon
            name="ListFilter"
            iconSet="Lucide"
            size={16}
            style={{ marginRight: 0 }}
          />
        </Button>
      </Input>
      {!_.isNil(modalOpen) && (
        <ListeParametrableModal
          selection="click"
          header={QUALIFICATION_ATTRIBUTES[qualification]?.title}
          icon={QUALIFICATION_ATTRIBUTES[qualification]?.icon}
          isSaveButtonDisabled={_.isEmpty(selectedItem)}
          onClose={handleModalClose}
          onSave={handleSave}
        >
          {listToRender}
        </ListeParametrableModal>
      )}
    </Form>
  );
};

export default QualificationInput;
